import { useState } from 'react';
import type { DigestChannel, DigestPreview, DigestSendResult, TelegramLinkCode, User } from '@crmsy/shared';
import { fetchDigestPreview, fetchMe, patchMe, sendDigestNow, telegramLinkCode, telegramUnlink } from '../lib/api.js';
import { useTelegramBot } from '../lib/features.js';
import { toast } from '../lib/toast.js';
import { THEMES, useTheme } from '../lib/theme.js';
import { useT, type Key } from '../i18n/index.js';
import { Modal } from '../components/Modal.jsx';

const field = 'w-full rounded-lg border border-line bg-surface px-3 py-2 text-sm outline-none focus:border-accent';

function timeZones(): string[] {
  const intl = Intl as unknown as { supportedValuesOf?: (k: string) => string[] };
  try {
    return intl.supportedValuesOf ? intl.supportedValuesOf('timeZone') : [];
  } catch {
    return [];
  }
}

/** Profile (§6.5): name, locale, time zone, Notion mapping (read-only), digest settings, appearance (D-24). */
export function ProfileView({ user, onUserUpdated }: { user: User; onUserUpdated: (u: User) => void }) {
  const t = useT();
  const [theme, setTheme] = useTheme();
  const [name, setName] = useState(user.name);
  const [locale, setLocale] = useState<'en' | 'bg'>(user.locale);
  const [timeZone, setTimeZone] = useState(user.timeZone);
  const [enabled, setEnabled] = useState(user.digest.enabled);
  const [hour, setHour] = useState(user.digest.hour);
  const [channel, setChannel] = useState<DigestChannel>(user.digest.channel);
  const [linkCode, setLinkCode] = useState<TelegramLinkCode | null>(null);
  const bot = useTelegramBot();
  const [phone, setPhone] = useState(user.digest.phone ?? '');
  const [key, setKey] = useState('');
  const [busy, setBusy] = useState<'save' | 'test' | 'preview' | null>(null);
  const [preview, setPreview] = useState<DigestPreview | null>(null);
  const zones = timeZones();

  const save = async () => {
    setBusy('save');
    try {
      const u = await patchMe({
        name: name.trim(),
        locale,
        timeZone: timeZone.trim(),
        digest: { enabled, hour, phone: phone.trim() || null, ...(user.telegram.linked ? { channel } : {}), ...(key.trim() ? { callMeBotKey: key.trim() } : {}) },
      });
      onUserUpdated(u);
      setKey('');
      toast(t('profile.saved'));
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), { kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const sendTest = async () => {
    setBusy('test');
    try {
      const r = await sendDigestNow();
      if (r.sent) toast(t('profile.testSent', { n: r.length ?? 0 }));
      else if (r.reason === 'no-key') toast(t('profile.testNoKey'), { kind: 'error' });
      else toast(t('profile.testFailed', { error: r.error ?? r.reason ?? '' }), { kind: 'error' });
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), { kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  const link = async () => {
    try {
      setLinkCode(await telegramLinkCode());
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), { kind: 'error' });
    }
  };
  const refreshLink = async () => {
    const u = await fetchMe();
    onUserUpdated(u);
    if (u.telegram.linked) {
      setLinkCode(null);
      if (u.digest.channel !== 'telegram') setChannel('telegram');
    }
  };
  const unlink = async () => {
    const u = await telegramUnlink();
    onUserUpdated(u);
    setChannel('callmebot');
    toast(t('profile.telegramUnlinked'));
  };

  const showPreview = async () => {
    setBusy('preview');
    try {
      setPreview(await fetchDigestPreview());
    } catch (e) {
      toast(e instanceof Error ? e.message : String(e), { kind: 'error' });
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="h-full overflow-y-auto p-4 md:p-6">
      <form
        className="mx-auto max-w-xl"
        onSubmit={(e) => {
          e.preventDefault();
          void save();
        }}
      >
        <h1 className="text-lg font-semibold">{t('profile.title')}</h1>
        <dl className="mt-3 grid grid-cols-[140px_1fr] gap-y-2 text-sm">
          <dt className="text-muted">{t('profile.email')}</dt>
          <dd>{user.email}</dd>
          <dt className="text-muted">{t('profile.role')}</dt>
          <dd>{user.role}</dd>
          <dt className="text-muted">{t('profile.notion')}</dt>
          <dd>{user.notionUserId ?? <span className="text-danger">{t('profile.unmapped')}</span>}</dd>
        </dl>

        <label className="mt-4 block">
          <span className="text-xs font-medium text-muted">{t('profile.name')}</span>
          <input value={name} onChange={(e) => setName(e.target.value)} className={`${field} mt-1`} name="name" maxLength={80} />
        </label>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-medium text-muted">{t('profile.locale')}</span>
            <select value={locale} onChange={(e) => setLocale(e.target.value as 'en' | 'bg')} className={`${field} mt-1`} name="locale">
              <option value="en">{t('profile.locale.en')}</option>
              <option value="bg">{t('profile.locale.bg')}</option>
            </select>
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted">{t('profile.tz')}</span>
            <input value={timeZone} onChange={(e) => setTimeZone(e.target.value)} list="tz-list" className={`${field} mt-1`} name="timeZone" placeholder={t('profile.tzPlaceholder')} />
            {zones.length > 0 && (
              <datalist id="tz-list">
                {zones.map((z) => (
                  <option key={z} value={z} />
                ))}
              </datalist>
            )}
          </label>
        </div>

        <h2 className="mt-6 text-xs font-semibold uppercase tracking-wide text-muted">{t('profile.digest')}</h2>
        <p className="mt-1 text-sm text-muted">{t('profile.digestLead')}</p>
        <div className="mt-2 flex flex-wrap items-center gap-3 text-sm">
          <label className="inline-flex items-center gap-2">
            <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} name="digestEnabled" />
            {t('profile.digestEnabled')}
          </label>
          <label className="inline-flex items-center gap-2">
            {t('profile.digestHour')}
            <select value={hour} onChange={(e) => setHour(Number(e.target.value))} className="rounded-lg border border-line bg-surface px-2 py-1 text-sm" name="digestHour">
              {Array.from({ length: 24 }, (_, h) => (
                <option key={h} value={h}>{String(h).padStart(2, '0')}:00</option>
              ))}
            </select>
          </label>
        </div>
        {user.telegram.linked && (
          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-sm" role="radiogroup" aria-label={t('profile.channel')}>
            <span className="text-xs font-medium text-muted">{t('profile.channel')}</span>
            {(['telegram', 'callmebot'] as DigestChannel[]).map((ch) => (
              <button key={ch} type="button" role="radio" aria-checked={channel === ch} className="chip" data-on={channel === ch} onClick={() => setChannel(ch)}>
                {t(`profile.channel.${ch}`)}
              </button>
            ))}
          </div>
        )}
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <label className="block">
            <span className="text-xs font-medium text-muted">{t('profile.phone')}</span>
            <input value={phone} onChange={(e) => setPhone(e.target.value)} className={`${field} mt-1`} name="phone" placeholder={t('profile.phonePlaceholder')} inputMode="tel" />
          </label>
          <label className="block">
            <span className="text-xs font-medium text-muted">{t('profile.key')}</span>
            <input value={key} onChange={(e) => setKey(e.target.value)} type="password" autoComplete="off" className={`${field} mt-1`} name="callMeBotKey" placeholder={user.digest.hasKey ? t('profile.keySet') : t('profile.keyUnset')} />
          </label>
        </div>
        <p className="mt-2 text-xs text-muted">{t('profile.howTo')}</p>
        {(user.digest.lastSentDate || user.digest.lastError) && (
          <p className="mt-1 text-xs text-muted">
            {user.digest.lastSentDate && t('profile.lastSent', { date: user.digest.lastSentDate })}
            {user.digest.lastError && <span className="text-danger"> {t('profile.lastError', { error: user.digest.lastError })}</span>}
          </p>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button type="submit" className="btn btn-primary" disabled={busy !== null}>
            {t('profile.save')}
          </button>
          <button type="button" className="btn" onClick={() => void showPreview()} disabled={busy !== null}>
            {t('profile.preview')}
          </button>
          <button type="button" className="btn" onClick={() => void sendTest()} disabled={busy !== null || !((user.digest.channel === 'telegram' && user.telegram.linked) || (user.digest.hasKey && !!user.digest.phone))} title={user.digest.hasKey || user.telegram.linked ? '' : t('profile.testNoKey')}>
            {t('profile.sendTest')}
          </button>
        </div>

        {bot && (
          <section data-telegram>
            <h2 className="mt-8 text-xs font-semibold uppercase tracking-wide text-muted">{t('profile.telegram')}</h2>
            <p className="mt-1 text-sm text-muted">{t('profile.telegramLead')}</p>
            {user.telegram.linked ? (
              <div className="mt-2 flex flex-wrap items-center gap-2 text-sm">
                <span>{t('profile.telegramLinked', { name: user.telegram.name ?? '' })}</span>
                <button type="button" className="btn" onClick={() => void unlink()}>{t('profile.telegramUnlink')}</button>
              </div>
            ) : linkCode ? (
              <div className="mt-2 rounded-lg border border-line bg-surface px-3 py-2 text-sm">
                <p>{t('profile.telegramCode', { code: linkCode.code })}</p>
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <a className="btn btn-primary" href={linkCode.url} target="_blank" rel="noopener">{t('profile.telegramOpen', { bot })}</a>
                  <code className="rounded bg-paper px-2 py-1 text-base font-semibold tracking-widest" data-link-code>{linkCode.code}</code>
                  <button type="button" className="btn" onClick={() => void refreshLink()}>{t('profile.telegramRefresh')}</button>
                </div>
              </div>
            ) : (
              <div className="mt-2">
                <button type="button" className="btn" onClick={() => void link()}>{t('profile.telegramLink')}</button>
              </div>
            )}
          </section>
        )}

        <h2 className="mt-8 text-xs font-semibold uppercase tracking-wide text-muted">{t('profile.appearance')}</h2>
        <div className="mt-2 flex flex-wrap gap-1.5" role="radiogroup" aria-label={t('profile.appearance')}>
          {THEMES.map((th) => (
            <button key={th} type="button" role="radio" aria-checked={theme === th} className="chip" data-on={theme === th} onClick={() => setTheme(th)}>
              {t(`theme.${th}` as Key)}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-muted">{t('profile.appearanceHint')}</p>
      </form>
      {preview && <DigestDialog preview={preview} onClose={() => setPreview(null)} />}
    </div>
  );
}

/** The digest as WhatsApp would show it (§6.2 "Send digest now", §6.5 preview). */
export function DigestDialog({ preview, onClose, sendAs }: { preview: DigestPreview; onClose: () => void; sendAs?: { name: string; send: () => Promise<DigestSendResult> } }) {
  const t = useT();
  const [busy, setBusy] = useState(false);
  const send = async () => {
    setBusy(true);
    try {
      const r = await (sendAs ? sendAs.send() : sendDigestNow());
      if (r.sent) {
        toast(t('digest.sent'));
        onClose();
      } else toast(t('profile.testFailed', { error: r.error ?? r.reason ?? '' }), { kind: 'error' });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal title={t('digest.title')} onClose={onClose} width="md">
      <p className="text-xs text-muted">{t('digest.lead', { n: preview.length })}</p>
      <pre className="mt-2 max-h-80 overflow-y-auto whitespace-pre-wrap rounded-lg bg-surface px-3 py-2 text-sm">{preview.text}</pre>
      {!preview.canSend && <p className="mt-2 text-xs text-muted">{t('digest.setup')}</p>}
      <div className="mt-3 flex justify-end gap-2">
        <button type="button" className="btn" onClick={onClose}>{t('dialog.cancel')}</button>
        <button type="button" className="btn btn-primary" onClick={() => void send()} disabled={!preview.canSend || busy}>{sendAs ? t('digest.sendTo', { name: sendAs.name }) : t('digest.send')}</button>
      </div>
    </Modal>
  );
}
